import type { ValidationIssue, ValidationReport } from "../engine/compileGame";

/**
 * Spec 016 US4 (T049) — renders the `compile_game` validation report for a
 * dropped `game.json`. Groups issues by severity and surfaces each one's stable
 * code, JSON-pointer location, message, detail, and suggested fixes — the
 * actionable feedback the validator was built to give (CLAUDE.md §5.3).
 *
 * On a clean compile (no errors) the panel still renders any warnings / info so
 * the designer sees the full picture; a fully-clean report shows a terse
 * "validated clean" confirmation.
 */
export function ValidationPanel({
  report,
  label,
  onDismiss,
}: {
  report: ValidationReport;
  /** Source filename, shown in the header. */
  label: string;
  onDismiss?: () => void;
}) {
  const { errors, warnings, info } = report;
  const total = errors.length + warnings.length + info.length;
  const clean = errors.length === 0;

  return (
    <section
      data-testid="validation-panel"
      role={errors.length > 0 ? "alert" : "status"}
      className="border border-paper-border bg-paper-panel p-3 text-sm dark:border-term-border dark:bg-term-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span
            className={`text-[11px] font-semibold uppercase tracking-widest ${
              clean
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-rose-700 dark:text-rose-400"
            }`}
          >
            {clean ? "✓ Validated" : "✗ Validation failed"}
          </span>
          <span className="truncate text-xs text-paper-hint dark:text-term-hint">
            {label}
          </span>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
          >
            Dismiss
          </button>
        )}
      </div>

      <p className="mt-1 text-xs text-paper-text dark:text-term-text">
        {errors.length} {plural(errors.length, "error")} · {warnings.length}{" "}
        {plural(warnings.length, "warning")} · {info.length} info
      </p>

      {total === 0 ? (
        <p className="mt-2 text-xs text-paper-hint dark:text-term-hint">
          No issues — the config compiled cleanly. Running the simulation now.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {errors.map((issue, i) => (
            <IssueRow key={`e-${issue.code}-${i}`} issue={issue} />
          ))}
          {warnings.map((issue, i) => (
            <IssueRow key={`w-${issue.code}-${i}`} issue={issue} />
          ))}
          {info.map((issue, i) => (
            <IssueRow key={`i-${issue.code}-${i}`} issue={issue} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** A single validator issue: code badge, location, message, detail, fixes. */
function IssueRow({ issue }: { issue: ValidationIssue }) {
  const tone =
    issue.severity === "error"
      ? "border-rose-300 dark:border-rose-700/50"
      : issue.severity === "warning"
        ? "border-amber-300 dark:border-amber-700/50"
        : "border-paper-border dark:border-term-border";
  return (
    <li
      data-testid="validation-issue"
      data-severity={issue.severity}
      data-code={issue.code}
      className={`border-l-2 ${tone} pl-2`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <code className="text-[11px] font-semibold text-paper-bright dark:text-term-bright">
          {issue.code}
        </code>
        {issue.location !== "" && (
          <code className="text-[10px] text-paper-hint dark:text-term-hint">
            {issue.location}
          </code>
        )}
      </div>
      <p className="text-xs text-paper-text dark:text-term-text">
        {issue.message}
      </p>
      {issue.detail !== "" && issue.detail !== issue.message && (
        <p className="mt-0.5 whitespace-pre-wrap text-[11px] text-paper-hint dark:text-term-hint">
          {issue.detail}
        </p>
      )}
      {issue.suggested_fixes.length > 0 && (
        <ul className="mt-0.5 space-y-0.5">
          {issue.suggested_fixes.map((fix, i) => (
            <li
              key={i}
              data-testid="validation-fix"
              className="text-[11px] text-emerald-700 dark:text-emerald-400"
            >
              ↳ {fix.description}
              <span className="ml-1 text-paper-hint dark:text-term-hint">
                ({Math.round(fix.confidence * 100)}%)
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}
